import omit from 'lodash/omit';
import {
  CreateMenuPayload,
  MenuResult,
  GroupResult,
  ItemResult,
  POSType,
  StoreOrderingForm,
  StoreOrderingRequest,
  StoreOrderingResponse,
  TransformMenuResult,
  TransformGroupResult,
  TransformItemResult,
  POSLocationExtendedAttributes,
} from './ordering-service.types';
import { getClientId } from 'util/client-id';
import { getMerchantId } from 'util/merchant-id';
import {
  transformHoursForRequest,
  transformTemporaryHoursForRequest,
} from './transformers/location/hours';
import {
  transformCatalogOverridesForRequest,
  transformGroupOverridesForRequest,
  transformMenuImportConfigForRequest,
  transformMenuImportConfigForResponse,
  transformMenuVisibilityForResponse,
} from './transformers/location/menus';
import {
  transformOrderProcessingConfigForRequest,
  transformOrderProcessingConfigForResponse,
} from './transformers/location/ordering';
import {
  transformChargesForRequest,
  transformChargesForResponse,
  transformDynamicDeliveryForRequest,
  transformTaxesForRequest,
  transformTaxesForResponse,
} from './transformers/location/payment-settings';
import {
  transformClosedTodayForResponse,
  transformDeliveryAreaForRequest,
  transformDeliveryRadius,
  transformOrderOptionsForRequest,
  transformOrderOptionsForResponse,
  transformOrderValueLimitsForRequest,
  transformOrderValueLimitsForResponse,
  transformPaymentConfigsForRequest,
  transformPaymentConfigsForResponse,
} from './transformers/location/pickup-delivery-settings';
import {
  transformEmailNotificationsForRequest,
  transformEmailNotificationsForResponse,
  transformExternalOrderingText,
} from './transformers/location/information';

export const transformOrderingResponse = (
  response: StoreOrderingResponse,
): StoreOrderingForm => {
  const pos = getPosSettings(response);

  return {
    ...response,
    pos,
    additionalExtendedAttributes: extendedAttributesToArray(
      response.extendedAttributes,
      getExcludedLocationProperties(pos.type),
    ),
    extendedAttributes: toLocationExtendedAttributes(
      response.extendedAttributes,
      pos.type,
    ),
    menuImportConfig: transformMenuImportConfigForResponse(response, pos),
    menuVisibility: transformMenuVisibilityForResponse(response),
    paymentGatewayConfig: {
      ...response.paymentGatewayConfig,
      additionalExtendedAttributes: extendedAttributesToArray(
        response.paymentGatewayConfig?.extendedAttributes,
      ),
    },
    showDeliveryTimeSlots: response?.showDeliveryTimeSlots,
    deliveryAsapSupported: response?.deliveryAsapSupported,
    pickupPaymentConfigs: transformPaymentConfigsForResponse(
      response?.paymentConfigs,
      'PICKUP',
    ),
    deliveryPaymentConfigs: transformPaymentConfigsForResponse(
      response?.paymentConfigs,
      'DELIVERY',
    ),
    orderNotificationRecipientEmail: transformEmailNotificationsForResponse(
      response?.orderNotificationRecipientEmailList,
    ),
    failureNotificationRecipientEmail: transformEmailNotificationsForResponse(
      response?.failureNotificationRecipientEmailList,
    ),
    pickupClosedToday: transformClosedTodayForResponse(response.pickupOffDays),
    deliveryClosedToday: transformClosedTodayForResponse(
      response.deliveryOffDays,
    ),
    taxes: transformTaxesForResponse(response),
    charges: transformChargesForResponse(response),
    enableTipping: !response.disableTipping,
    title: response.locationName,
    pickupOrderValueLimits: transformOrderValueLimitsForResponse(
      response.pickupOrderValueLimits,
    ),
    pickupOrderOptions: transformOrderOptionsForResponse(
      response.pickupOrderOptions,
    ),
    deliveryOrderValueLimits: transformOrderValueLimitsForResponse(
      response.deliveryOrderValueLimits,
    ),
    deliveryOrderOptions: transformOrderOptionsForResponse(
      response.deliveryOrderOptions,
    ),
    orderProcessingConfig: transformOrderProcessingConfigForResponse(
      response.orderProcessingConfig,
    ),
  };
};

export const transformOrderingRequest = (
  body: StoreOrderingForm,
): StoreOrderingRequest => {
  // combine array extended attributes with pos-specific extended attributes
  const locationExtendedAttributes = {
    ...additionalPropertiesToObject(body.additionalExtendedAttributes),
    ...transformExtendedAttributes(body.extendedAttributes),
  };

  // set the store id separately in extended attributes if it's a pos that requires it
  if (posLocationGuidMapping[body.pos.type]?.location) {
    locationExtendedAttributes[posLocationGuidMapping[body.pos.type].location] =
      body.pos.storeId as any;
  }

  body.extPosLocationId =
    body.pos.type === 'incentivio-order'
      ? null
      : body.pos.storeId ?? body.extPosLocationId;

  return {
    ...omit(body, [
      'pos',
      'additionalExtendedAttributes',
      'pickupPaymentConfigs',
      'deliveryPaymentConfigs',
      'orderNotificationRecipientEmail',
      'failureNotificationRecipientEmail',
      'pickupClosedToday',
      'deliveryClosedToday',
      'orderProcessingConfig',
    ]),
    extendedAttributes: locationExtendedAttributes,
    menuImportConfig: transformMenuImportConfigForRequest(body),
    paymentGatewayConfig: {
      ...body.paymentGatewayConfig,
      extendedAttributes: additionalPropertiesToObject(
        body.paymentGatewayConfig?.additionalExtendedAttributes,
      ),
    },
    pickupOrderHours: transformHoursForRequest(body.pickupOrderHours),
    deliveryOrderHours: transformHoursForRequest(body.deliveryOrderHours),
    pickupTemporaryHours: transformTemporaryHoursForRequest(
      body.pickupTemporaryHours,
    ),
    deliveryTemporaryHours: transformTemporaryHoursForRequest(
      body.deliveryTemporaryHours,
    ),
    paymentConfigs: transformPaymentConfigsForRequest(
      body.pickupPaymentConfigs,
      body.deliveryPaymentConfigs,
      body.pickupOrdersAccepted,
      body.deliveryOrdersAccepted,
      body.paymentConfigs,
    ),
    orderNotificationRecipientEmailList: transformEmailNotificationsForRequest(
      body.orderNotificationRecipientEmail,
    ),
    failureNotificationRecipientEmailList:
      transformEmailNotificationsForRequest(
        body.failureNotificationRecipientEmail,
      ),
    taxes: transformTaxesForRequest(body.taxes),
    charges: transformChargesForRequest(body.charges),
    dynamicDeliveryCharge: transformDynamicDeliveryForRequest(body.charges),
    disableTipping: !body.enableTipping,
    catalogOverrides: transformCatalogOverridesForRequest(body.menuVisibility),
    groupOverrides: transformGroupOverridesForRequest(body.menuVisibility),
    pickupOrderValueLimits: transformOrderValueLimitsForRequest(
      body.pickupOrderValueLimits,
    ),
    pickupOrderOptions: transformOrderOptionsForRequest(
      body.pickupOrderOptions,
    ),
    deliveryOrderValueLimits: transformOrderValueLimitsForRequest(
      body.deliveryOrderValueLimits,
    ),
    deliveryOrderOptions: transformOrderOptionsForRequest(
      body.deliveryOrderOptions,
    ),
    orderProcessingConfig: transformOrderProcessingConfigForRequest(
      body.orderProcessingConfig,
      body.pos.type,
    ),
    deliveryRadius: transformDeliveryRadius(
      body.deliveryArea,
      body.deliveryRadius,
    ),
    deliveryArea: transformDeliveryAreaForRequest(body.deliveryArea),
    externalOrderingText: transformExternalOrderingText(
      body.externalOrderingEnabled,
      body.externalOrderingText,
    ),
  };
};

export const extendedAttributesToArray = (
  ex: Record<string, any> = {},
  excludeKeys: string[] = [],
) =>
  ex
    ? Object.entries(ex).reduce((acc, [key, value]) => {
        if (!excludeKeys.includes(key)) {
          acc.push({ key, value });
        }
        return acc;
      }, [])
    : [];

export const additionalPropertiesToObject = (
  ex: { key: string; value: any }[] = [],
) =>
  ex?.reduce((acc, { key, value }) => {
    if (value !== '' && value != null) {
      let modifiedValue = value;
      if (typeof value === 'boolean') {
        modifiedValue = value.toString();
      }
      acc[key] = modifiedValue.toString();
    }
    return acc;
  }, {}) ?? {};

export const transformExtendedAttributes = (ex: Record<string, any> = {}) =>
  Object.entries(ex).reduce((acc, [key, value]) => {
    if (value !== '' && value != null) {
      let modifiedValue = value;
      if (typeof value === 'boolean') {
        modifiedValue = value.toString();
      }
      acc[key] = modifiedValue.toString();
    }
    return acc;
  }, {});

export const getPosSettings = (
  response: StoreOrderingResponse,
): StoreOrderingForm['pos'] => {
  const type =
    response.menuImportConfig?.connectorGatewayName ||
    response.orderProcessingConfig?.orderGatewayName ||
    'incentivio-order';
  const locationGuidMapping = posLocationGuidMapping[type] ?? {};

  return {
    type,
    storeId:
      response.menuImportConfig?.extendedAttributes?.[
        locationGuidMapping?.menu
      ] ?? '',
  };
};

export const getExcludedLocationProperties = (posType: POSType) => [
  ...Object.keys(specialLocationProperties[posType] ?? {}),
  ...(posLocationGuidMapping[posType]?.location
    ? [posLocationGuidMapping[posType].location]
    : []),
];

export const toLocationExtendedAttributes = (
  extendedAttributes: Record<string, string>,
  posType: POSType,
) => {
  if (!extendedAttributes) return {};

  const includedFields = getExcludedLocationProperties(posType);
  const extendedAttributesArray = Object.entries(extendedAttributes);

  const filtered = extendedAttributesArray.filter(([key]) =>
    includedFields.includes(key),
  );

  const transformed = filtered.map(([key, value]) => {
    if (specialLocationProperties[posType]?.[key] === 'switch') {
      return [key, value.toLowerCase() === 'true' ? true : false];
    }
    return [key, value];
  });

  return Object.fromEntries(transformed);
};

export const posLocationGuidMapping: {
  [K in POSType]?: {
    location?: string;
    menu?: string;
  };
} = {
  'incentivio-connector-ncc': {
    // location: '',
    // menu: '',
  },
  'incentivio-conpos-brink': {
    // location: '',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-lightspeed': {
    // location: '',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-qu': {
    location: 'EXT_STORE_GUID',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-revel': {
    // location: '',
    menu: 'ESTABLISHMENT',
  },
  'incentivio-conpos-silverware': {
    location: 'OnlineOrdUser',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-otter': {
    location: 'EXT_STORE_GUID',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-square': {
    location: 'SQUARE_LOCATION_ID',
    menu: 'SQUARE_LOCATION_ID',
  },
  'incentivio-connector-spoton-pos': {
    location: 'EXT_STORE_GUID',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-connector-toast': {
    location: 'TOAST_RESTAURANT_ID',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-connector-simphony-pos': {
    location: 'EXT_STORE_GUID',
    menu: 'EXT_STORE_GUID',
  },
  'incentivio-conpos-clover': {
    // location: '',
    menu: 'EXT_STORE_GUID',
  },
};

export type ExtendedAttributeControlType =
  | 'text'
  | 'switch'
  | 'select'
  | 'number';

export const specialLocationProperties: {
  [K in POSType]: Partial<
    Record<keyof POSLocationExtendedAttributes<K>, ExtendedAttributeControlType>
  >;
} = {
  '': {},
  'incentivio-order': {},
  'incentivio-connector-ncc': {
    special_instructions_item_id: 'text',
    order_type_id: 'text',
    tender_id: 'text',
  },
  'incentivio-conpos-brink': {
    DELIVERY_DESTINATION_ID: 'text',
    PICKUP_DESTINATION_ID: 'text',
    TENDER_ID: 'text',
    LOCATION_TOKEN: 'text',
    OPEN_DISCOUNT_ID: 'text',
    API_BASE_URL: 'text',
    ORDER_NOTE_ITEM_ID: 'text',
    GIFT_CARD_TENDER_ID: 'text',
  },
  'incentivio-conpos-lightspeed': {
    EXTERNAL_BUSINESS_NAME: 'text',
    LIGHTSPEED_BASE_URL: 'text',
    LIGHTSPEED_COMPANY_ID: 'text',
    LIGHTSPEED_DEVICE_ID: 'text',
    LIGHTSPEED_NOTE_ID: 'text',
    LIGHTSPEED_PASSWORD: 'text',
    LIGHTSPEED_PAYMENT_TYPEID: 'text',
    LIGHTSPEED_PAYMENT_TYPE_TYPEID: 'text',
    LIGHTSPEED_USER_NAME: 'text',
  },
  'incentivio-conpos-qu': {
    DELIVERY_ORDER_TYPE_ID: 'text',
    EXT_COMPANY_ID: 'text',
    EXT_MEAL_PERIOD: 'text',
    EXT_ORDER_CHANNEL: 'text',
    EXT_ORDER_TYPE: 'text',
    EXT_PASSWORD: 'text',
    EXT_PAYMENT_TYPE: 'text',
    // EXT_STORE_GUID: 'text',
    EXT_USER_NAME: 'text',
    HOSTED_PAYMENT_HTML_PAGE: 'text',
    PICKUP_ORDER_TYPE_ID: 'text',
    OVERRIDE_DISPLAY_RANK: 'switch',
  },
  'incentivio-conpos-revel': {
    POS_CONFIGURED_PAYMENT_TYPE: 'text',
  },
  'incentivio-conpos-silverware': {
    ord2StationID: 'text',
    // OnlineOrdUser: 'text',
    OrdItmNoteItem: 'text',
    Order2Pickup: 'text',
    Order2Delivery: 'text',
    StoreAlias: 'text',
    SILVERWARE_PAYMENTTYPE_UUID: 'text',
    EXTERNAL_BUSINESS_NAME: 'text',
    visa: 'text',
    american_express: 'text',
    master: 'text',
    discover: 'text',
    'Cash (USD)': 'text',
    CLOUD_IMPORT_URL: 'text',
    CLOUD_IMPORT_TOKEN: 'text',
    gift_card: 'text',
    CHECK_LEVEL_DISCOUNT_ITEM_ID: 'text',
    CHECK_LEVEL_DISCOUNT_ITEM_PRICE_ID: 'text',
  },
  'incentivio-conpos-otter': {
    // EXT_STORE_GUID: 'text'
  },
  'incentivio-conpos-square': {
    // SQUARE_LOCATION_ID: 'text'
  },
  'incentivio-connector-spoton-pos': {
    // EXT_STORE_GUID: 'text',
    DELIVERY_ORDER_TYPE_ID: 'text',
    PICKUP_ORDER_TYPE_ID: 'text',
  },
  'incentivio-connector-toast': {
    // TOAST_RESTAURANT_ID: 'text',
    PICKUP_DINING_OPTION_GUID: 'text',
    DELIVERY_DINING_OPTION_GUID: 'text',
    OTHER_PAYMENT_GUID: 'text',
  },
  'incentivio-connector-simphony-pos': {},
  'incentivio-conpos-clover': {},
};

export const transformMenusPayload = (body: CreateMenuPayload) => {
  return {
    ...body,
    extendedAttributes: additionalPropertiesToObject(body.extendedAttributes),
    privateAttributes: additionalPropertiesToObject(body.privateAttributes),
    clientId: getClientId(),
    merchantId: getMerchantId(),
  };
};

export const transformGetMenuResponse = (
  response: MenuResult,
): TransformMenuResult => ({
  ...response,
  extendedAttributes: extendedAttributesToArray(response.extendedAttributes),
  privateAttributes: extendedAttributesToArray(response.privateAttributes),
  displayInfo: response.displayInfo.map(({ langCode, ...rest }) => ({
    ...rest,
    languageCode: langCode,
  })),
});

export const transformGetGroupResponse = (
  response: GroupResult,
): TransformGroupResult => ({
  ...response,
  extendedAttributes: extendedAttributesToArray(response?.extendedAttributes),
  privateAttributes: extendedAttributesToArray(response?.privateAttributes),
  displayInfo: response?.displayInfo?.map(({ langCode, ...rest }) => ({
    ...rest,
    languageCode: langCode,
  })),
});

export const transformItemResponse = (
  response: ItemResult,
): TransformItemResult => ({
  ...response,
  price: response?.price / 1000,
  extendedAttributes: extendedAttributesToArray(response?.extendedAttributes),
  privateAttributes: extendedAttributesToArray(response?.privateAttributes),
  displayInfo: response?.displayInfo?.map(
    ({ langCode, shortDescription, longDescription, ...rest }) => ({
      ...rest,
      languageCode: langCode,
      shortDescription: shortDescription ?? '',
      longDescription: longDescription ?? '',
    }),
  ),
});
